import React, { useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { DjayAvatar, useDjaySelection } from '../mascot';
import { useAppStore } from '../app/hooks/useAppStore';
import { MODULES, getFamiliesForModule, getAllowedTiersForGroup, getModuleIcon, getModuleColors } from '../content';
import { getFamilyMasteryKey } from '../mastery';
import { getMasteryLabelInfo } from './shared/masteryLabelInfo';
interface FluencyModulePickerProps {
  isOpen: boolean;
  onSelectAll: () => void;
  onSelectModule: (moduleId: string) => void;
  onCancel: () => void;
}
export function FluencyModulePicker({
  isOpen,
  onSelectAll,
  onSelectModule,
  onCancel
}: FluencyModulePickerProps) {
  const {
    selectedVariant
  } = useDjaySelection();
  const {
    state,
    selectedGroup
  } = useAppStore();
  const familyMastery = state.familyMastery || {};
  const allowedTiers = useMemo(() => getAllowedTiersForGroup(selectedGroup), [selectedGroup]);
  const moduleAggregates = useMemo(() => {
    return MODULES.map(module => {
      const families = getFamiliesForModule(module.id);
      const moduleFamilies = families.filter(family => {
        if (!allowedTiers.has(family.tier)) return false;
        const masteryKey = getFamilyMasteryKey(family.familyId, selectedGroup);
        const mastery = familyMastery[masteryKey];
        return mastery && mastery.attempts > 0;
      });
      const count = moduleFamilies.length;
      const avgMastery = count > 0 ? moduleFamilies.reduce((sum, f) => {
        const masteryKey = getFamilyMasteryKey(f.familyId, selectedGroup);
        return sum + (familyMastery[masteryKey]?.masteryScore ?? 0);
      }, 0) / count : 0;
      return {
        module,
        count,
        avgMastery
      };
    }).filter(m => m.count > 0);
  }, [selectedGroup, familyMastery, allowedTiers]);
  return <AnimatePresence data-id="element-2760">
      {isOpen && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-2761">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onCancel} data-id="element-2762" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} className="relative w-full max-w-3xl bg-slate-50 rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]" data-id="element-2763">
            {/* Header */}
            <div className="bg-gradient-to-r from-amber-400 to-orange-400 p-6 text-white text-center relative flex-shrink-0" data-id="element-2764">
              <button onClick={onCancel} className="absolute top-4 right-4 p-2 bg-white/20 hover:bg-white/30 rounded-full transition-colors" data-id="element-2765">
                <X className="w-5 h-5" data-id="element-2766" />
              </button>
              <div className="flex justify-center mb-3" data-id="element-2767">
                <DjayAvatar size="md" emotion="pointing" variant={selectedVariant} animate={true} data-id="element-2768" />
              </div>
              <h2 className="text-2xl font-black tracking-tight" data-id="element-2769">
                Wat wil je oefenen?
              </h2>
            </div>

            {/* Content */}
            <div className="p-6 sm:p-8 overflow-y-auto" data-id="element-2770">
              <Button variant="primary" onClick={onSelectAll} className="w-full py-4 mb-8 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 border-none shadow-lg shadow-amber-200 text-lg" data-id="element-2771">
                <Sparkles className="w-5 h-5 mr-2" data-id="element-2772" />
                Alles door elkaar
              </Button>

              {moduleAggregates.length > 0 && <div data-id="element-2773">
                  <h3 className="text-lg font-black text-slate-800 mb-4 text-center" data-id="element-2774">
                    Of kies een specifiek vak
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-id="element-2775">
                    {moduleAggregates.map(({
                module,
                count,
                avgMastery
              }, index) => {
                const ModIcon = getModuleIcon(module.icon);
                const colors = getModuleColors(module.color);
                const masteryInfo = getMasteryLabelInfo(avgMastery);
                return <motion.button key={module.id} initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: Math.min(index * 0.05, 0.3)
                }} onClick={() => onSelectModule(module.id)} className="w-full text-left p-4 rounded-3xl border-2 border-slate-200 bg-white hover:border-slate-300 hover:shadow-lg active:scale-[0.98] transition-all group relative overflow-hidden flex flex-col h-full" data-id="element-2776">
                            <div className="flex items-start justify-between mb-3" data-id="element-2777">
                              <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-2xl flex items-center justify-center ${colors.light}`} data-id="element-2778">
                                <ModIcon className={`w-5 h-5 sm:w-6 sm:h-6 ${colors.text}`} data-id="element-2779" />
                              </div>
                              <div className="bg-slate-100 text-slate-500 text-[10px] font-bold px-2 py-1 rounded-lg" data-id="element-2780">
                                {count} items
                              </div>
                            </div>

                            <h3 className="text-sm sm:text-base font-black text-slate-800 mb-1 group-hover:text-slate-900 transition-colors" data-id="element-2781">
                              {module.title}
                            </h3>

                            <div className="mt-auto pt-3" data-id="element-2782">
                              <div className="flex items-center justify-between mb-1.5" data-id="element-2783">
                                <span className={`text-[10px] sm:text-xs font-bold ${masteryInfo.color}`} data-id="element-2784">
                                  {masteryInfo.label}
                                </span>
                                <span className="text-[10px] font-bold text-slate-400" data-id="element-2785">
                                  {Math.round(avgMastery)}%
                                </span>
                              </div>
                              <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden" data-id="element-2786">
                                <motion.div className="h-full rounded-full" style={{
                        backgroundColor: masteryInfo.ring
                      }} initial={{
                        width: 0
                      }} animate={{
                        width: `${Math.min(100, Math.max(0, avgMastery))}%`
                      }} transition={{
                        duration: 0.8,
                        ease: 'easeOut'
                      }} data-id="element-2787" />
                              </div>
                            </div>
                          </motion.button>;
              })}
                  </div>
                </div>}
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}